// NotesApp source file: calendar.component.ts

import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DatePickerModule } from 'primeng/datepicker';

import { DataService, Note, Tag } from '../../../_service/data.service';
import { FilterParams } from '../../../_service/filter.service';

export interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  dayNumber: number;
}

export interface CalendarWeek {
  days: CalendarDay[];
}

const DAY_NAMES_LONG = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const INACTIVE_STATUSES = new Set(['Completed', 'Obsolete', 'Cancelled', 'Expired']);
const MONTH_ABBR_TO_INDEX: Record<string, number> = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  sept: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

@Component({
  selector: 'app-calendar-view',
  imports: [CommonModule, FormsModule, RouterModule, ButtonModule, DialogModule, DatePickerModule],
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
})
export class CalendarViewComponent implements OnInit, OnChanges {
  private readonly router = inject(Router);
  private readonly dataService = inject(DataService);

  @Input() notes: Note[] = [];
  @Input() tags: Tag[] = [];
  @Input() filter: FilterParams = {};
  @Input() initialMonth: string | null = null;
  @Input() initialVisibility: 'calendar' | 'all' = 'calendar';

  @Output() monthChanged = new EventEmitter<string>();
  @Output() visibilityChanged = new EventEmitter<'calendar' | 'all'>();

  /** The month/year currently shown in the calendar. */
  viewDate: Date = new Date();

  /** Fixed reference to today (not reactive to keep stable). */
  readonly today: Date = new Date();

  weeks: CalendarWeek[] = [];

  readonly dayNamesLong = DAY_NAMES_LONG;

  goToDialogVisible = false;
  goToDate: Date | null = null;
  visibilityMode: 'calendar' | 'all' = 'calendar';
  creatingNoteForDay: string | null = null;

  private notesByDay = new Map<string, Note[]>();
  private tagsById = new Map<string, Tag>();
  private tagsBySlug = new Map<string, Tag>();
  private tagsByName = new Map<string, Tag>();

  // ─── Computed labels ──────────────────────────────────────────────────────

  get monthLabel(): string {
    return this.viewDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  }

  get isShowingCurrentMonth(): boolean {
    return (
      this.viewDate.getFullYear() === this.today.getFullYear() &&
      this.viewDate.getMonth() === this.today.getMonth()
    );
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.visibilityMode = this.initialVisibility === 'all' ? 'all' : 'calendar';
    this.viewDate = this.parseMonthKey(this.initialMonth) ?? new Date(this.today.getFullYear(), this.today.getMonth(), 1);
    this.rebuildNotesIndex();
    this.buildCalendar();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialVisibility'] && !changes['initialVisibility'].firstChange) {
      this.visibilityMode = this.initialVisibility === 'all' ? 'all' : 'calendar';
      this.rebuildNotesIndex();
      this.buildCalendar();
    }

    if (changes['initialMonth'] && !changes['initialMonth'].firstChange) {
      const parsedMonth = this.parseMonthKey(this.initialMonth);
      if (parsedMonth) {
        this.viewDate = parsedMonth;
        this.buildCalendar();
      }
    }

    if (changes['notes'] || changes['tags']) {
      this.rebuildNotesIndex();
      this.buildCalendar();
    }
  }

  // ─── Navigation ─────────────────────────────────────────────────────────────

  navigatePrev(): void {
    this.setViewDate(new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() - 1, 1));
  }

  navigateNext(): void {
    this.setViewDate(new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1, 1));
  }

  goToToday(): void {
    this.setViewDate(new Date(this.today.getFullYear(), this.today.getMonth(), 1));
  }

  // ─── Go To dialog ───────────────────────────────────────────────────────────

  openGoToDialog(): void {
    this.goToDate = new Date(this.viewDate);
    this.goToDialogVisible = true;
  }

  applyGoTo(): void {
    if (this.goToDate) {
      this.setViewDate(new Date(this.goToDate.getFullYear(), this.goToDate.getMonth(), 1));
    }
    this.goToDialogVisible = false;
  }

  cancelGoTo(): void {
    this.goToDialogVisible = false;
  }

  getNotesForDay(date: Date): Note[] {
    return this.notesByDay.get(this.toDayKey(date)) ?? [];
  }

  trackByNoteId(_index: number, note: Note): string {
    return note._id;
  }

  isInactiveForDisplay(note: Note): boolean {
    return INACTIVE_STATUSES.has(String(note.Status || '').trim());
  }

  noteTitle(note: Note): string {
    const title = String(note.Title ?? '').trim();
    return title || '(untitled)';
  }

  noteHoverText(note: Note): string {
    const title = this.noteTitle(note);
    const status = String(note.Status ?? '').trim() || '(no status)';
    return `Title: ${title} | Status: ${status}`;
  }

  setVisibilityMode(mode: 'calendar' | 'all'): void {
    const nextMode = mode === 'all' ? 'all' : 'calendar';
    if (this.visibilityMode === nextMode) {
      return;
    }

    this.visibilityMode = nextMode;
    this.rebuildNotesIndex();
    this.buildCalendar();
    this.visibilityChanged.emit(this.visibilityMode);
  }

  openNoteFromCalendar(note: Note, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.router.navigate(['/view', note._id], {
      state: { filter: this.filter },
    });
  }

  createNoteForDay(date: Date, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    const dayKey = this.toDayKey(date);
    if (this.creatingNoteForDay) {
      return;
    }

    this.creatingNoteForDay = dayKey;
    this.dataService.createNote().subscribe({
      next: async (resp) => {
        const note = resp?.data;
        const noteId = String(note?._id ?? '').trim();
        if (!note || !noteId) {
          this.creatingNoteForDay = null;
          return;
        }

        note.ReferenceDate = dayKey;
        const calendarTagId = this.resolveCalendarTagId();
        if (calendarTagId) {
          const existingTags = Array.isArray(note.Tags) ? note.Tags : [];
          const existingTagIds = new Set(
            existingTags
              .map((entry) => (typeof entry === 'string' ? entry.trim() : String((entry as any)?._id || '').trim()))
              .filter((value) => !!value),
          );
          if (!existingTagIds.has(calendarTagId)) {
            note.Tags = [...existingTags, calendarTagId];
          }
        }

        try {
          await this.dataService.saveNoteOffline(note, true);
        } finally {
          this.creatingNoteForDay = null;
        }

        this.router.navigate(['/edit', noteId], {
          state: { filter: this.filter },
        });
      },
      error: () => {
        this.creatingNoteForDay = null;
      },
    });
  }

  // ─── Calendar grid builder ──────────────────────────────────────────────────

  private buildCalendar(): void {
    this.weeks = [];
    const year = this.viewDate.getFullYear();
    const month = this.viewDate.getMonth();
    const lastDay = new Date(year, month + 1, 0); // last day of month
    const todayStr = this.today.toDateString();

    // Find the Monday on or before the first day of the month.
    // JS getDay(): 0=Sun, 1=Mon … 6=Sat  →  ISO offset: Mon=0 … Sun=6
    const firstDay = new Date(year, month, 1);
    const jsDay = firstDay.getDay();
    const mondayOffset = jsDay === 0 ? 6 : jsDay - 1;
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - mondayOffset);

    const current = new Date(startDate);

    // Build week rows until we've included the last day of the month.
    // Always complete the final row to a full 7-day week.
    while (current <= lastDay || (this.weeks.length > 0 && this.weeks[this.weeks.length - 1].days.length < 7)) {
      if (this.weeks.length === 0 || this.weeks[this.weeks.length - 1].days.length === 7) {
        this.weeks.push({ days: [] });
      }
      const week = this.weeks[this.weeks.length - 1];
      week.days.push({
        date: new Date(current),
        isCurrentMonth: current.getMonth() === month,
        isToday: current.toDateString() === todayStr,
        dayNumber: current.getDate(),
      });
      current.setDate(current.getDate() + 1);
    }
  }

  private rebuildNotesIndex(): void {
    this.notesByDay.clear();
    this.tagsById = new Map((this.tags || []).map((tag) => [tag._id, tag]));
    this.tagsBySlug = new Map();
    this.tagsByName = new Map();

    for (const tag of this.tags || []) {
      const slugKey = String(tag.slug || '').trim().toLowerCase();
      const nameKey = String(tag.name || '').trim().toLowerCase();
      if (slugKey) {
        this.tagsBySlug.set(slugKey, tag);
      }
      if (nameKey) {
        this.tagsByName.set(nameKey, tag);
      }
    }

    for (const note of this.notes || []) {
      if (!this.shouldDisplayInCalendar(note)) {
        continue;
      }

      // Fall back to created_on when no explicit ReferenceDate is set
      // (mirrors Edit behaviour: auto-fills ReferenceDate from created_on on save)
      const referenceDate = this.parseReferenceDate(note.ReferenceDate || note.created_on);
      if (!referenceDate) {
        continue;
      }

      const dayKey = this.toDayKey(referenceDate);
      const existing = this.notesByDay.get(dayKey) ?? [];
      existing.push(note);
      this.notesByDay.set(dayKey, existing);
    }

    for (const notesForDay of this.notesByDay.values()) {
      notesForDay.sort((a, b) => this.noteTitle(a).localeCompare(this.noteTitle(b)));
    }
  }

  private shouldDisplayInCalendar(note: Note): boolean {
    if (!note || this.isDeleted(note)) {
      return false;
    }

    if (this.visibilityMode === 'all') {
      return true;
    }

    return this.hasCalendarTag(note);
  }

  private setViewDate(date: Date): void {
    this.viewDate = new Date(date.getFullYear(), date.getMonth(), 1);
    this.buildCalendar();
    this.monthChanged.emit(this.toMonthKey(this.viewDate));
  }

  private parseMonthKey(value: string | null | undefined): Date | null {
    const raw = String(value ?? '').trim();
    const match = raw.match(/^(\d{4})-(\d{2})$/);
    if (!match) {
      return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
      return null;
    }

    return new Date(year, month - 1, 1);
  }

  private toMonthKey(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    return `${year}-${month}`;
  }

  private hasCalendarTag(note: Note): boolean {
    const tagRefs = Array.isArray(note.Tags) ? note.Tags : [];
    for (const tagRef of tagRefs) {
      if (typeof tagRef === 'string') {
        const trimmed = tagRef.trim();
        if (!trimmed) {
          continue;
        }

        const key = trimmed.toLowerCase();
        const resolved = this.tagsById.get(trimmed) ?? this.tagsBySlug.get(key) ?? this.tagsByName.get(key);

        if (resolved && String(resolved.tagType || '').trim().toLowerCase() === 'calendar') {
          return true;
        }

        // Legacy fallback: some older notes may persist plain tag names (e.g. "Calendar").
        if (key === 'calendar') {
          return true;
        }

        continue;
      }

      if (tagRef && typeof tagRef === 'object') {
        const embeddedType = String(tagRef.tagType || '').trim().toLowerCase();
        if (embeddedType === 'calendar') {
          return true;
        }

        // Re-resolve against current canonical tag by _id — handles the case where
        // the embedded tag object has a stale tagType from before it was reclassified.
        const embeddedId = String((tagRef as any)._id || '').trim();
        if (embeddedId) {
          const canonical = this.tagsById.get(embeddedId);
          if (canonical && String(canonical.tagType || '').trim().toLowerCase() === 'calendar') {
            return true;
          }
        }

        const nameOrSlug = String(tagRef.name || tagRef.slug || '').trim().toLowerCase();
        if (nameOrSlug === 'calendar') {
          return true;
        }
        // Resolve embedded name/slug against canonical tags
        if (nameOrSlug) {
          const canonical = this.tagsByName.get(nameOrSlug) ?? this.tagsBySlug.get(nameOrSlug);
          if (canonical && String(canonical.tagType || '').trim().toLowerCase() === 'calendar') {
            return true;
          }
        }
      }
    }
    return false;
  }

  private resolveCalendarTagId(): string | null {
    const byNameOrSlug = (this.tags || []).find((tag) => {
      const name = String(tag?.name || '').trim().toLowerCase();
      const slug = String(tag?.slug || '').trim().toLowerCase();
      return name === 'calendar' || slug === 'calendar';
    });
    if (byNameOrSlug?._id) {
      return String(byNameOrSlug._id);
    }

    const byType = (this.tags || []).find((tag) => String(tag?.tagType || '').trim().toLowerCase() === 'calendar');
    if (byType?._id) {
      return String(byType._id);
    }

    // Fallback for legacy setups where notes stored plain string tags.
    return 'Calendar';
  }

  private isDeleted(note: Note): boolean {
    return String(note.Status || '').trim() === 'Deleted' || String(note.syncStatus || '').trim() === 'deleted';
  }

  private parseReferenceDate(value?: string): Date | null {
    if (!value) {
      return null;
    }

    const raw = String(value).trim();
    if (!raw) {
      return null;
    }

    // Plain ISO date should be interpreted literally with no timezone conversion.
    const isoDateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoDateOnly) {
      const year = Number(isoDateOnly[1]);
      const month = Number(isoDateOnly[2]);
      const day = Number(isoDateOnly[3]);
      return this.safeDate(year, month, day);
    }

    // ISO date-time (often stored via toISOString from local picker) should use local day.
    // Example: local 2025-08-11 might be stored as 2025-08-10T22:00:00.000Z.
    if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
      const parsedIsoDateTime = new Date(raw);
      if (!Number.isNaN(parsedIsoDateTime.getTime())) {
        return new Date(
          parsedIsoDateTime.getFullYear(),
          parsedIsoDateTime.getMonth(),
          parsedIsoDateTime.getDate(),
        );
      }
    }

    // dd.mm.yyyy or dd/mm/yyyy or dd-mm-yyyy
    const dmyNumeric = raw.match(/^(\d{1,2})[.\/-](\d{1,2})[.\/-](\d{4})$/);
    if (dmyNumeric) {
      const day = Number(dmyNumeric[1]);
      const month = Number(dmyNumeric[2]);
      const year = Number(dmyNumeric[3]);
      return this.safeDate(year, month, day);
    }

    // dd.Mon.yyyy, e.g. 10.Aug.2025
    const dmyText = raw.match(/^(\d{1,2})[.\/-]([A-Za-z]{3,9})[.\/-](\d{4})$/);
    if (dmyText) {
      const day = Number(dmyText[1]);
      const monthToken = String(dmyText[2]).trim().toLowerCase();
      const year = Number(dmyText[3]);
      const monthIndex = MONTH_ABBR_TO_INDEX[monthToken];
      if (Number.isInteger(monthIndex)) {
        return this.safeDate(year, monthIndex + 1, day);
      }
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    // Last fallback: compare by local calendar day only.
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }

  private safeDate(year: number, month1Based: number, day: number): Date | null {
    if (!Number.isFinite(year) || !Number.isFinite(month1Based) || !Number.isFinite(day)) {
      return null;
    }

    const date = new Date(year, month1Based - 1, day);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month1Based - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date;
  }

  private toDayKey(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
